import { useCallback, useEffect, useMemo, useState } from 'react';
import { beforeReleaseItems, type DateRedoState } from 'fym-shared/src/music24/dateRedo';
import { useGetDateRedoQuery } from '../../../../../../redux/api/fymAPI';
import { useIsDemoAccount } from '../../../../../accountSelector/useIsDemoAccount';
import { useRealtimePollInterval } from '../../../../../realtime/useRealtimePollInterval';
import { redoBannerState, type CoverRedoBannerState } from '../coverRedo/coverRedoBannerState';
import { dateRedoMemory } from '../coverRedo/coverRedoMemory';

/** The poll while a redo runs and the socket is down. */
const WORKING_POLL_MS = 15_000;

/** True for an answer of the date redo route (an older API answers 404, a test server `{}`). Pure. */
export function isDateRedoState(value: unknown): value is DateRedoState {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const answer = value as Partial<DateRedoState>;
  return typeof answer.outOfDate === 'boolean' && Array.isArray(answer.items) && typeof answer.released === 'boolean';
}

/** The key of the offer that "Not now" puts away: the release date of the song now. Pure. */
export function dateRedoOfferKey(state: DateRedoState | undefined): string {
  return state?.releaseDate ?? 'none';
}

/**
 * The date redo state of one song for the Kit and the Posts tab ("Redo the kit with the new release
 * date"): the state answer, the banner state (the rules of the cover redo, `redoBannerState`), "Not
 * now" for this release date, the close of an end card, the quiet line after "Not now", and the
 * files of the "Before release" group. The demo artist reads nothing. The `ReleaseKit` tag of the
 * song follows the kit and song events, so the answer stays live; a 15 s poll runs only while a
 * redo works and the socket is down.
 */
export function useDateRedo(input: { accountId: number; catalogItemUuid: string }) {
  const { accountId, catalogItemUuid } = input;
  const isDemo = useIsDemoAccount();
  const [isWorking, setIsWorking] = useState(false);
  const pollingInterval = useRealtimePollInterval(isWorking ? WORKING_POLL_MS : 0);
  const { data: answer } = useGetDateRedoQuery({ accountId, catalogItemUuid }, { skip: isDemo, pollingInterval });
  const data = isDateRedoState(answer) ? answer : undefined;
  const running = data?.run?.state === 'working';
  useEffect(() => {
    setIsWorking(running);
  }, [running]);
  const [memory, setMemory] = useState(() => ({
    dismissed: dateRedoMemory.readDismissed(catalogItemUuid),
    seenRun: dateRedoMemory.readSeenRun(catalogItemUuid),
  }));

  const offerKey = dateRedoOfferKey(data);
  const banner: CoverRedoBannerState = useMemo(() => redoBannerState(data, offerKey, memory), [data, memory, offerKey]);
  const showsQuietLine = banner === 'hidden' && data !== undefined && data.outOfDate && memory.dismissed === offerKey;
  const beforeRelease = useMemo(() => beforeReleaseItems(data?.items ?? []), [data]);

  const notNow = useCallback(() => {
    dateRedoMemory.writeDismissed(catalogItemUuid, offerKey);
    setMemory((current) => ({ ...current, dismissed: offerKey }));
  }, [catalogItemUuid, offerKey]);

  const closeEnd = useCallback(() => {
    const runId = data?.run?.runId;
    if (runId === undefined) {
      return;
    }
    dateRedoMemory.writeSeenRun(catalogItemUuid, runId);
    setMemory((current) => ({ ...current, seenRun: runId }));
  }, [catalogItemUuid, data]);

  return { state: data, banner, showsQuietLine, beforeRelease, notNow, closeEnd };
}

export type DateRedo = ReturnType<typeof useDateRedo>;
